import { Link } from 'react-router-dom';
import { ChevronRight, Wifi, WifiOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/Badge';
import { expiryStatus } from '@/lib/license';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { Device } from '@/lib/types';

interface Props { device: Device; index?: number }

export function DeviceCard({ device, index = 0 }: Props) {
  const lic = expiryStatus(device.expires_at);
  const online = !!device.online;
  const tone = lic.kind === 'expired' ? 'bad' : lic.kind === 'warn' ? 'warn' : lic.kind === 'active' ? 'ok' : 'neutral';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.04, 0.3) }}
    >
      <Link
        to={`/devices/${encodeURIComponent(device.client_id)}`}
        className={cn(
          'group block bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md',
          'hover:border-slate-300 transition-all duration-200',
          'border-l-[6px]',
          online ? 'border-l-ok' : 'border-l-slate-300',
        )}
      >
        <div className="px-5 py-4 flex items-center gap-4">
          <div className={cn(
            'shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
            online ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400',
          )}>
            {online ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-ink text-base truncate">{device.client_name}</h3>
              <Badge tone={online ? 'ok' : 'neutral'} dot>
                {online ? 'Online' : 'Offline'}
              </Badge>
              {lic.kind !== 'none' && (
                <Badge tone={tone}>{lic.text}</Badge>
              )}
            </div>
            <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
              <span className="font-mono">{device.client_id}</span>
              <span>·</span>
              <span>Last seen {relativeTime(device.last_seen)}</span>
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
        </div>
      </Link>
    </motion.div>
  );
}
